"use client"

import type React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import { Card, CardBody, CardHeader, CardTitle, Badge, Input, Row, Col } from "reactstrap"
import DataTable, { type TableColumn } from "react-data-table-component"
import { FaPlus, FaEye, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSearch, FaFilter, FaSort } from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import { MenuService } from "../../../Service/role-management-service"
import type { Menu, ListParams } from "../rolemanagement/types"
import Swal from "sweetalert2"
import { toast } from "react-toastify"

interface MenuListProps {
  onAdd: () => void
  onEdit: (menu: Menu) => void
  onView: (menu: Menu) => void
  refreshTrigger: boolean
}

const MenuList: React.FC<MenuListProps> = ({ onAdd, onEdit, onView, refreshTrigger }) => {
  // State management
  const [data, setData] = useState<Menu[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [sortField, setSortField] = useState<string>("sort_order")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  // Fetch data with enhanced parameters
  const fetchData = useCallback(async () => {
    setLoading(true)

    try {
      const params: ListParams = {
        page: currentPage,
        limit: perPage,
        search: search || undefined,
        ordering: sortDirection === "desc" ? `-${sortField}` : sortField,
      }

      if (statusFilter !== "all") {
        params.is_active = statusFilter === "active"
      }

      const response = await MenuService.getAll(params)
      setData(response.results)
      setTotalRows(response.count)
    } catch (error: any) {
      console.error("Failed to fetch menus:", error)
      toast.error("Failed to load menus")
      setData([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [currentPage, perPage, search, statusFilter, sortField, sortDirection])

  // Effects
  useEffect(() => {
    fetchData()
  }, [fetchData, refreshTrigger])

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1)
      fetchData()
    }, 500)

    return () => clearTimeout(delayedSearch)
  }, [search])

  // Handle menu status toggle
  const handleToggleStatus = async (menu: Menu) => {
    const action = menu.is_active ? "deactivate" : "activate"

    try {
      await MenuService.update(menu.id, { is_active: !menu.is_active })
      toast.success(`Menu ${action}d successfully`)
      fetchData()
    } catch (error: any) {
      toast.error(`Failed to ${action} menu`)
    }
  }

  // Handle menu deletion
  const handleDelete = async (menu: Menu) => {
    const result = await Swal.fire({
      title: "Delete Menu?",
      html: `
        <div class="text-start">
          <p>This will permanently remove <strong>"${menu.title}"</strong>.</p>
          ${
            menu.children && menu.children.length > 0
              ? '<div class="alert alert-warning"><small><i class="fa fa-exclamation-triangle"></i> This menu has child items that will also be deleted!</small></div>'
              : ""
          }
          <div class="alert alert-danger">
            <small><i class="fa fa-exclamation-triangle"></i> This action cannot be undone!</small>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
      focusCancel: true,
    })

    if (!result.isConfirmed) return

    try {
      await MenuService.delete(menu.id)
      await Swal.fire({
        title: "Deleted!",
        text: "Menu has been removed successfully.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      fetchData()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to delete menu"
      Swal.fire("Error", errorMessage, "error")
    }
  }

  // Handle sorting
  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    setSortField(column.selector)
    setSortDirection(sortDirection)
  }

  // Get menu type color
  const getMenuTypeColor = (type: string) => {
    switch (type) {
      case "section":
        return "primary"
      case "sub":
        return "info"
      case "link":
        return "success"
      default:
        return "secondary"
    }
  }

  // Get menu hierarchy display
  const getMenuHierarchy = (menu: Menu) => {
    if (menu.parent_name) {
      return `${menu.parent_name} > ${menu.title}`
    }
    return menu.title
  }

  // Filter data by type
  const filteredData = useMemo(() => {
    if (typeFilter === "all") return data
    return data.filter((menu) => menu.menu_type === typeFilter)
  }, [data, typeFilter])

  // Table columns
  const columns: TableColumn<Menu>[] = useMemo(
    () => [
      {
        name: "Menu",
        selector: (row: Menu) => row.title,
        sortable: true,
        cell: (row: Menu) => (
          <div className="py-2">
            <div className="d-flex align-items-center">
              {row.icon && <i className={`${row.icon} me-2 text-muted`} />}
              <div>
                <div className="fw-medium">{getMenuHierarchy(row)}</div>
                <div className="small text-muted">{row.name}</div>
              </div>
            </div>
          </div>
        ),
        width: "300px",
      },
      {
        name: "Type",
        selector: (row: Menu) => row.menu_type,
        sortable: true,
        cell: (row: Menu) => (
          <Badge color={getMenuTypeColor(row.menu_type)} pill className="text-capitalize px-3 py-2">
            {row.menu_type}
          </Badge>
        ),
        width: "120px",
      },
      {
        name: "URL",
        selector: (row: Menu) => row.url || "",
        cell: (row: Menu) => (
          <div>
            {row.url ? (
              <code className="small bg-light px-2 py-1 rounded">{row.url}</code>
            ) : (
              <span className="text-muted small">—</span>
            )}
          </div>
        ),
        width: "200px",
      },
      {
        name: "Order",
        selector: (row: Menu) => row.sort_order,
        sortable: true,
        cell: (row: Menu) => (
          <div className="text-center">
            <Badge color="secondary" outline className="px-3 py-2">
              {row.sort_order}
            </Badge>
          </div>
        ),
        width: "100px",
      },
      {
        name: "Status",
        selector: (row: Menu) => row.is_active,
        sortable: true,
        cell: (row: Menu) => (
          <div className="d-flex flex-column gap-1">
            <Badge color={row.is_active ? "success" : "secondary"} pill className="px-3 py-2">
              {row.is_active ? "Active" : "Inactive"}
            </Badge>
            {row.is_visible && (
              <Badge color="info" outline className="small px-2 py-1">
                Visible
              </Badge>
            )}
          </div>
        ),
        width: "120px",
      },
      {
        name: "Actions",
        cell: (row: Menu) => (
          <div className="d-flex gap-1">
            <Btn color="info" size="xs" onClick={() => onView(row)} title="View Details" className="btn-icon">
              <FaEye size={12} />
            </Btn>
            <Btn color="warning" size="xs" onClick={() => onEdit(row)} title="Edit Menu" className="btn-icon">
              <FaEdit size={12} />
            </Btn>
            <Btn
              color={row.is_active ? "secondary" : "success"}
              size="xs"
              onClick={() => handleToggleStatus(row)}
              title={row.is_active ? "Deactivate" : "Activate"}
              className="btn-icon"
            >
              {row.is_active ? <FaToggleOff size={12} /> : <FaToggleOn size={12} />}
            </Btn>
            <Btn color="danger" size="xs" onClick={() => handleDelete(row)} title="Delete Menu" className="btn-icon">
              <FaTrash size={12} />
            </Btn>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "220px",
      },
    ],
    [onView, onEdit],
  )

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <CardTitle tag="h4" className="mb-1 text-primary">
              Menu Management
            </CardTitle>
            <p className="text-muted mb-0 small">Manage system navigation menus • {totalRows} total menus</p>
          </div>
          <Btn color="primary" onClick={onAdd} className="btn-lg">
            <FaPlus className="me-2" />
            Add New Menu
          </Btn>
        </div>
      </CardHeader>

      <CardBody>
        {/* Enhanced Filters */}
        <Row className="mb-4">
          <Col md="4">
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Input
                type="search"
                placeholder="Search menus by title or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-5"
              />
            </div>
          </Col>
          <Col md="3">
            <div className="position-relative">
              <FaFilter className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Input type="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="ps-5">
                <option value="all">All Types</option>
                <option value="section">Sections</option>
                <option value="sub">Submenus</option>
                <option value="link">Links</option>
              </Input>
            </div>
          </Col>
          <Col md="3">
            <div className="position-relative">
              <FaSort className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="ps-5"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </Input>
            </div>
          </Col>
          <Col md="2">
            <div className="text-end">
              <small className="text-muted">
                Showing {filteredData.length} of {totalRows}
              </small>
            </div>
          </Col>
        </Row>

        {/* Enhanced Data Table */}
        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={(newPerPage, page) => {
            setPerPage(newPerPage)
            setCurrentPage(page)
          }}
          onSort={handleSort}
          sortServer
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fa fa-list fa-3x"></i>
              </div>
              <h5 className="text-muted">No menus found</h5>
              <p className="text-muted">
                {search ? "Try adjusting your search criteria" : "Get started by creating your first menu"}
              </p>
              {!search && (
                <Btn color="primary" onClick={onAdd} className="mt-2">
                  <FaPlus className="me-2" />
                  Create First Menu
                </Btn>
              )}
            </div>
          }
          customStyles={{
            headRow: {
              style: {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
              },
            },
            headCells: {
              style: {
                fontWeight: "600",
                fontSize: "0.875rem",
                color: "#495057",
              },
            },
            rows: {
              style: {
                minHeight: "60px",
              },
            },
          }}
        />
      </CardBody>
    </Card>
  )
}

export default MenuList
